import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { createContext, useEffect, useRef, useState } from 'react';
import AppHeader from '../components/GrandeEcole/AppHeader';
import AppMain from '../components/GrandeEcole/AppMain';
import AppMobileNav from '../components/GrandeEcole/AppMobileNav';
import AppSidebar from '../components/GrandeEcole/AppSidebar';
import ProfileOnboardingForm from '../components/GrandeEcole/ProfileOnboardingForm';
import ProfileSelectionScreen from '../components/GrandeEcole/ProfileSelectionScreen';
import { getProfile } from '../config/activityProfileConfig2';
import { useAuth } from '../contexts/AuthContext';
import useGrandeEcole from '../stores/useGrandeEcole';

// ─── Contexte onboarding ─────────────────────────────────────────────────────
// Sorti au niveau module pour être stable (pas recréé à chaque render)
export const OnboardingContext = createContext(null);

// ─── OnboardingPrompt ────────────────────────────────────────────────────────
export function OnboardingPrompt({ profile, onConfigureNow, onConfigureLater }) {
  const label = profile?.label || 'votre profil';
  const icon  = profile?.icon  || '⚙️';

  return (
    <div className="min-h-screen bg-[#020617] text-cyan-50 relative overflow-hidden flex items-center justify-center p-4">
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `
          linear-gradient(rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(rgba(34,211,238,0.015) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.015) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px, 80px 80px, 16px 16px, 16px 16px',
      }} />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_30%_20%,rgba(34,211,238,0.07),transparent_55%)]" />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_70%_80%,rgba(245,158,11,0.04),transparent_55%)]" />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-lg bg-[#020617]/90 backdrop-blur-md border border-cyan-500/10 p-6 sm:p-8 text-center"
        style={{ boxShadow: '0 0 60px rgba(34,211,238,0.05)' }}
      >
        <span className="absolute top-0 left-0 w-5 h-5 border-t border-l border-cyan-500/30" />
        <span className="absolute top-0 right-0 w-5 h-5 border-t border-r border-cyan-500/30" />
        <span className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-amber-500/25" />
        <span className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-amber-500/25" />

        <div className="text-5xl mb-6">
          {typeof icon === 'string' && icon.startsWith('http')
            ? <img src={icon} alt="" className="w-12 h-12 mx-auto" />
            : <span>{icon}</span>
          }
        </div>

        <h2
          className="font-black uppercase tracking-tight text-white mb-4"
          style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(1.3rem, 4vw, 1.75rem)' }}
        >
          Bienvenue sur {label}
        </h2>

        <p className="text-cyan-100/60 italic text-sm leading-relaxed mb-2">
          Voulez-vous configurer vos informations de certification maintenant ?
        </p>
        <p className="text-[10px] font-mono uppercase tracking-wider text-cyan-500/40 mb-8">
          (Vous pourrez le faire plus tard depuis le tableau de bord)
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onConfigureNow}
            className="group relative flex items-center justify-center gap-2 px-6 py-3 overflow-hidden font-black uppercase text-white text-sm tracking-wider transition-all"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)' }}
          >
            <motion.span
              className="absolute inset-0 opacity-0 group-hover:opacity-100"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(245,158,11,0.08), transparent)' }}
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
            />
            <Sparkles size={15} className="text-amber-400 relative z-10" />
            <span className="relative z-10">Configurer maintenant</span>
            <ArrowRight size={15} className="text-amber-400 group-hover:translate-x-1 transition-transform relative z-10" />
          </button>

          <button
            onClick={onConfigureLater}
            className="relative px-6 py-3 font-mono uppercase tracking-widest text-slate-400 text-xs transition-all"
            style={{ border: '1px solid rgba(34,211,238,0.1)', background: 'transparent' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(34,211,238,0.22)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(34,211,238,0.1)'; }}
          >
            Plus tard
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function GrandeEcoleV2() {
  const { user } = useAuth();

  const {
    activityProfile,
    hasChosenProfile,
    setActivityProfile,
    initialize,
    startMonitoring,
    stopMonitoring,
    onboardingCompleted,
    tenantOnboardingData,
    layout,
  } = useGrandeEcole();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isEditing,        setIsEditing]        = useState(false);

  // BUG 3 FIX — activeSection initialisé à '' et mis à jour une seule fois
  // quand layout.homeModule est disponible (après initialize())
  const [activeSection, setActiveSection] = useState('');
  const homeSectionSet = useRef(false);
  useEffect(() => {
    if (!homeSectionSet.current && layout?.homeModule) {
      setActiveSection(layout.homeModule);
      homeSectionSet.current = true;
    }
  }, [layout?.homeModule]);

  // Machine à états principale
  const [step, setStep] = useState('profileSelection');

  // BUG 1 FIX — openOnboarding stable, passé via contexte monté en permanence
  const openOnboarding = () => setStep('form');

  const toggleEdit        = () => setIsEditing(prev => !prev);
  const handleSaveSection = async () => { setIsEditing(false); };
  const handleCancelEdit  = () => setIsEditing(false);

  // Initialisation unique
  useEffect(() => {
    initialize();
    startMonitoring();
    return () => stopMonitoring();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // BUG 2 FIX — on ajoute onboardingCompleted et tenantOnboardingData aux deps
  // pour que le routing re-tourne après updateTenantOnboarding
  useEffect(() => {
    if (!hasChosenProfile || !activityProfile) return;

    const profile       = getProfile(activityProfile);
    const hasOnboarding = (profile?.onboarding?.fields?.length > 0) || !!profile?.personas;
    const isComplete    = onboardingCompleted && tenantOnboardingData?.profileId === activityProfile;
    const dismissed     = localStorage.getItem(`promptDismissed_${activityProfile}`) === 'true';

    if (!hasOnboarding || isComplete || dismissed) {
      setStep('dashboard');
    } else if (step !== 'form') {
      // Ne revient pas sur 'prompt' si l'utilisateur est déjà dans le formulaire
      setStep('prompt');
    }
  }, [hasChosenProfile, activityProfile, onboardingCompleted, tenantOnboardingData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleProfileSelect = async (profileId) => {
    await setActivityProfile(profileId);
    // L'effet ci-dessus prend le relais quand hasChosenProfile passe à true
  };

  const handlePromptConfigureNow = () => setStep('form');

  const handlePromptLater = () => {
    localStorage.setItem(`promptDismissed_${activityProfile}`, 'true');
    setStep('dashboard');
  };

  const handleOnboardingComplete = () => {
    // updateTenantOnboarding a déjà mis onboardingCompleted à true dans le store
    // → l'useEffect va détecter isComplete=true et passer à 'dashboard'
    // On force quand même pour éviter un frame de décalage
    localStorage.removeItem(`promptDismissed_${activityProfile}`);
    setStep('dashboard');
  };

  const handleOnboardingSkip = () => setStep('dashboard');

  // ── Rendu ────────────────────────────────────────────────────────────────────
  const resolvedProfile = getProfile(activityProfile);

  // BUG 1 FIX — le Provider est toujours monté, DashboardEngine peut
  // appeler useContext(OnboardingContext) sans risque de null crash
  return (
    <OnboardingContext.Provider value={{ openOnboarding }}>
      {(() => {
        switch (step) {
          case 'profileSelection':
            return <ProfileSelectionScreen onSelect={handleProfileSelect} />;

          case 'prompt':
            return (
              <OnboardingPrompt
                profile={resolvedProfile}          // BUG 2bis FIX : on passe l'objet, pas l'id
                onConfigureNow={handlePromptConfigureNow}
                onConfigureLater={handlePromptLater}
              />
            );

          case 'form':
            return (
              <ProfileOnboardingForm
                profileId={activityProfile}
                onComplete={handleOnboardingComplete}
                onSkip={handleOnboardingSkip}
              />
            );

          case 'dashboard':
          default:
            return (
              <div className="min-h-screen font-mono bg-[#161b22] text-zinc-300 flex flex-col">
                <AppHeader
                  sidebarCollapsed={sidebarCollapsed}
                  onToggleSidebar={() => setSidebarCollapsed(c => !c)}
                  activeSection={activeSection}
                  isEditing={isEditing}
                  onToggleEdit={toggleEdit}
                  onSave={handleSaveSection}
                  onCancel={handleCancelEdit}
                />
                <div className="flex flex-1 overflow-hidden">
                  <AppSidebar
                    collapsed={sidebarCollapsed}
                    activeSection={activeSection}
                    onNavigate={setActiveSection}
                  />
                  <main className="flex-1 overflow-y-auto p-4 md:p-6">
                    <AppMain
                      activeSection={activeSection}
                      isEditing={isEditing}
                      handleSaveSection={handleSaveSection}
                      toggleEdit={toggleEdit}
                    />
                  </main>
                </div>
                <AppMobileNav
                  activeSection={activeSection}
                  onNavigate={setActiveSection}
                />
              </div>
            );
        }
      })()}
    </OnboardingContext.Provider>
  );
}